import { Pageable } from 'aos-helpers/src/helpers/Pageable'
import { format, Query } from 'url'

import {
    AnyTaskTemplate,
    KanbanTask,
    TaskAssignee,
    TaskStatusUpdateDto,
    TaskTemplateFormPayload,
    TaskTemplateListItem,
} from '../../services/tasks/types'
import { BaseRestService } from '../base/BaseRestService'
import { MapTaskDto } from './types/MapTaskDto'
import { TaskDto } from './types/TaskDto'
import { TaskSnapshotDto } from './types/TaskSnapshotDto'

class TasksRestServiceClass extends BaseRestService {
    public loadTasks(filter: string, query: Query): Promise<Pageable<TaskSnapshotDto>> {
        return this.getAndTransform<Pageable<TaskSnapshotDto>>(
            format({
                pathname: `/task-manager/tasks/${filter}`,
                query,
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public createTask(payload: object): Promise<TaskDto> {
        return this.postJsonAndTransform<TaskDto>(
            format({
                pathname: '/task-manager/tasks',
            }),
            payload,
            BaseRestService.jsonTransformer,
        )
    }

    public updateTask(id: string, payload: object): Promise<TaskDto> {
        return this.patchJsonAndTransform<TaskDto>(
            format({
                pathname: `/task-manager/tasks/${id}`,
            }),
            payload,
            BaseRestService.jsonTransformer,
        )
    }

    public deleteTask(id: string): Promise<object> {
        return this.deleteReq(
            format({
                pathname: `/task-manager/tasks/${id}`,
            }),
        )
    }

    public loadTask(id: string): Promise<TaskDto> {
        return this.getAndTransform<TaskDto>(
            format({
                pathname: `/task-manager/tasks/${id}`,
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public loadTimelineTasks(query: Query): Promise<TaskSnapshotDto[]> {
        return this.getAndTransform<TaskSnapshotDto[]>(
            format({
                pathname: '/task-manager/tasks/timeline',
                query,
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public getRecentTasks(query: Query): Promise<TaskSnapshotDto[]> {
        return this.getAndTransform<TaskSnapshotDto[]>(
            format({
                pathname: '/task-manager/tasks/recent',
                query,
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public getUpcomingTasks(query: Query): Promise<TaskSnapshotDto[]> {
        return this.getAndTransform<TaskSnapshotDto[]>(
            format({
                pathname: '/task-manager/tasks/upcoming',
                query,
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public changeStatus(id: string, status: TaskStatusUpdateDto): Promise<Response> {
        return this.postJson(
            format({
                pathname: `/task-manager/tasks/${id}/status`,
            }),
            status,
        )
    }

    public sendMessage(id: string, message: string): Promise<Response> {
        return this.postJson(
            format({
                pathname: `/task-manager/tasks/${id}/message`,
            }),
            { message },
        )
    }

    public loadMapTasks(bbox: string): Promise<MapTaskDto[]> {
        return this.getAndTransform<MapTaskDto[]>(
            format({
                pathname: '/task-manager/tasks/map',
                query: { bbox },
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public exportTasks(query: Query): Promise<object> {
        return this.get(
            format({
                pathname: '/export-service/export',
                query: this.transformedQuery(query),
            }),
        )
    }

    public exportTaskLogsForTask(id: number): Promise<[Blob, string]> {
        const headers = new Headers()
        headers.set('Accept', 'application/pdf')
        return this.getAndTransform(
            format({
                pathname: `/task-manager/tasks/${id}/export`,
            }),
            BaseRestService.fileDownloadTransformer,
            headers,
        )
    }

    public changeTaskChecklistItem(
        taskId: number,
        checklistId: number,
        itemId: number,
        done: boolean,
    ): Promise<TaskDto> {
        return this.patchJsonAndTransform<TaskDto>(
            `/task-manager/tasks/${taskId}/checklists/${checklistId}/items/${itemId}`,
            { done },
            BaseRestService.jsonTransformer,
        )
    }

    public changeRecurrentTaskChecklistItem(
        parentTaskId: number,
        instanceId: number,
        checklistId: number,
        itemId: number,
        done: boolean,
    ): Promise<TaskDto> {
        return this.patchJsonAndTransform<TaskDto>(
            `/task-manager/tasks/${parentTaskId}/${instanceId}/checklists/${checklistId}/items/${itemId}`,
            { done },
            BaseRestService.jsonTransformer,
        )
    }

    public firstChangeRecurrentTaskChecklistItem(
        parentTaskId: number,
        instanceId: number,
        templateId: number,
        seq: number,
        done: boolean,
    ): Promise<TaskDto> {
        return this.patchJsonAndTransform<TaskDto>(
            `/task-manager/tasks/${parentTaskId}/${instanceId}/checklists/template/${templateId}/seq/${seq}`,
            { done },
            BaseRestService.jsonTransformer,
        )
    }
    public getWeeklyTasks(query?: Query): Promise<KanbanTask[]> {
        return this.getAndTransform<KanbanTask[]>(
            format({
                pathname: `/task-manager/tasks/kanban-board`,
                query: this.transformedQuery({
                    ...query,
                }),
            }),
            BaseRestService.jsonTransformer,
        )
    }
    public getTaskTemplates(query?: Query): Promise<Pageable<TaskTemplateListItem>> {
        return this.getAndTransform<Pageable<TaskTemplateListItem>>(
            format({
                pathname: `/task-manager/tasks/template`,
                query: this.transformedQuery({
                    ...query,
                }),
            }),
            BaseRestService.jsonTransformer,
        )
    }
    public getTaskTemplatesAssignees(siteLocation: string): Promise<TaskAssignee[]> {
        return this.getAndTransform<TaskAssignee[]>(
            format({
                pathname: `/task-manager/tasks/template/assignees`,
                query: this.transformedQuery({ siteLocation }),
            }),
            BaseRestService.jsonTransformer,
        )
    }

    public getTaskTemplateDetails(id: number): Promise<AnyTaskTemplate> {
        return this.getAndTransform<AnyTaskTemplate>(
            format({
                pathname: `/task-manager/tasks/template/${id}`,
            }),
            BaseRestService.jsonTransformer,
        )
    }
    public createTaskTemplate(payload: TaskTemplateFormPayload): Promise<AnyTaskTemplate> {
        return this.postJsonAndTransform<AnyTaskTemplate>(
            format({
                pathname: `/task-manager/tasks/template`,
            }),
            payload,
            BaseRestService.jsonTransformer,
        )
    }

    public updateTaskTemplate(
        id: number,
        payload: TaskTemplateFormPayload,
    ): Promise<AnyTaskTemplate> {
        return this.patchJsonAndTransform<AnyTaskTemplate>(
            format({
                pathname: `/task-manager/tasks/template/${id}`,
            }),
            payload,
            BaseRestService.jsonTransformer,
        )
    }

    public deleteTaskTemplate(id: number) {
        return this.deleteReq(`/task-manager/tasks/template/${id}`)
    }
}

export const tasksRestService = new TasksRestServiceClass()
